'use client';

import { usePublicContent } from '@/lib/use-public-content';
import { AboutSectionTabEnum } from '@/types/enums';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import AboutSectionTabButton from './AboutSectionTabButton';
import AboutSectionTabContent from './AboutSectionTabContent';

export default function AboutSection({ q1CertificatePdf }: { q1CertificatePdf: string }) {
    const t = useTranslations();
    const q1 = usePublicContent('q1-certificate', { pdf: q1CertificatePdf });

    const [activeTab, setActiveTab] = useState<string>(AboutSectionTabEnum.AboutUs);

    const handleTabClick = (id: string) => {
        if (activeTab !== id) {
            setActiveTab(id);
            window.history.pushState(null, '', `#${id}`);
        }
    };

    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (aboutTabs(t).some((tab) => tab.id === hash)) {
            setActiveTab(hash);
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [t]);

    return (
        <section className="bg-white w-full">
            <div className="flex justify-between max-lg:flex-wrap">
                {aboutTabs(t).map(({ id, label, imageSrc }) => (
                    <AboutSectionTabButton
                        id={id}
                        label={label}
                        activeTab={activeTab}
                        imageSrc={imageSrc}
                        onTabClick={() => handleTabClick(id)}
                        key={id}
                    />
                ))}
            </div>

            <div className="tab-content container mx-auto px-0 max-lg:px-4 pb-20">
                {activeTab === AboutSectionTabEnum.AboutUs && (
                    <AboutSectionTabContent
                        imageSrc="/img/about-1.webp"
                        title={t.rich('About.modernApproachToEngineering', {
                            strong: (chunks) => (
                                <span className="font-medium text-brand">{chunks}</span>
                            ),
                        })}
                        description={t('About.descriptionAboutUsDescription')}
                        services={[
                            {
                                title: t('About.workOrganization'),
                                description: t('About.weSupport'),
                                imageSrc: '/img/services/1.webp',
                            },
                            {
                                title: t('About.professionalEngineers'),
                                description: t('About.ourEngineeringDepartment'),
                                imageSrc: '/img/services/3.webp',
                            },
                        ]}
                    />
                )}
                {activeTab === AboutSectionTabEnum.QualityAssurance && (
                    <AboutSectionTabContent
                        imageSrc="/img/about-2.webp"
                        title={t.rich('About.ensuringPremiumQuality', {
                            strong: (chunks) => (
                                <span className="font-medium text-brand">{chunks}</span>
                            ),
                        })}
                        description={t('About.qaDescription')}
                        services={[
                            {
                                title: t('About.analytical'),
                                description: t('About.analyticalMethods'),
                                imageSrc: '/img/services/1.webp',
                            },
                            {
                                title: t('About.qualityPolicy'),
                                description: t('About.theFoundationOfOurSuccess'),
                                imageSrc: '/img/services/3.webp',
                            },
                        ]}
                    />
                )}
                {activeTab === AboutSectionTabEnum.DataSecurity && (
                    <AboutSectionTabContent
                        imageSrc="/img/about-3.webp"
                        title={t.rich('About.dataSecurityIsMatter', {
                            strong: (chunks) => (
                                <span className="font-medium text-brand">{chunks}</span>
                            ),
                        })}
                        description={t('About.dataSecurityDescription')}
                        services={[
                            {
                                title: t('About.security'),
                                description: t('About.ourClientsInclude'),
                                imageSrc: '/img/services/1.webp',
                            },
                            {
                                title: t('About.encryption'),
                                description: t('About.sslEncryption'),
                                imageSrc: '/img/services/3.webp',
                            },
                        ]}
                    />
                )}
                {activeTab === AboutSectionTabEnum.Q1Supplier && (
                    <AboutSectionTabContent
                        imageSrc="/img/about-4.webp"
                        title={t.rich('About.q1SupplierToDeutscheBahnAG', {
                            strong: (chunks) => (
                                <span className="font-medium text-brand">{chunks}</span>
                            ),
                        })}
                        description={t('About.q1Description')}
                        services={[
                            {
                                title: t('About.workOrganization'),
                                description: t('About.weSupport'),
                                imageSrc: '/img/services/1.webp',
                            },
                            {
                                title: t('About.professionalEngineers'),
                                description: t('About.ourEngineeringDepartment'),
                                imageSrc: '/img/services/3.webp',
                            },
                        ]}
                        certificatePdf={q1.pdf}
                        downloadCertificateLabel={t('About.downloadCertificate')}
                    />
                )}
            </div>
        </section>
    );
}

const aboutTabs = (t: Function) => [
    {
        id: AboutSectionTabEnum.AboutUs,
        label: t('About.aboutUs'),
        imageSrc: '/img/services/6.webp',
    },
    {
        id: AboutSectionTabEnum.QualityAssurance,
        label: t('SingleService.qualityAssurance'),
        imageSrc: '/img/services/5.webp',
    },
    {
        id: AboutSectionTabEnum.DataSecurity,
        label: t('Metadata.About.headings.dataSecurity'),
        imageSrc: '/img/services/7.webp',
    },
    {
        id: AboutSectionTabEnum.Q1Supplier,
        label: t('About.q1Supplier'),
        imageSrc: '/img/services/2.webp',
    },
];
